import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import { PENDING_SUBSCRIPTION_ONBOARDING_KEY } from '../../../Utils/authStorage';
import { isSuperAdmin, getSuperAdminHomePath } from '../../../Utils/superAdminAccess';
import { SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY } from '../../../Utils/importTreeSideLock';
import './../../../Auth.css';
import AuthLeftBranding from './AuthLeftBranding';
import AuthLandingFooter from './AuthLandingFooter';
import AuthBackToHome from './AuthBackToHome';
import useNoFocusZoomOnIOS from '../../../Utils/useNoFocusZoomOnIOS';
import { clearBirthdaysTodayDismissed } from "../../../Utils/dashboardBirthByDay";

function Login() {
    useNoFocusZoomOnIOS();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({ email: '', password: '' });
    const [loader, setLoader] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isSafeRedirect = (pathname) => {
        if (!pathname || !pathname.startsWith('/')) return false;
        const unsafe = ['/login', '/register', '/forgot-password', '/reset-password', '/select-role'];
        return !unsafe.includes(pathname);
    };

    const getRedirectPath = () => {
        const from = location.state?.from;
        if (from?.pathname && isSafeRedirect(from.pathname)) {
            return (from.pathname || '') + (from.search || '');
        }
        return null;
    };

    const getSuperAdminRedirectPath = () => {
        const redirectTo = getRedirectPath();
        if (redirectTo?.startsWith('/super-admin')) return redirectTo;
        return getSuperAdminHomePath();
    };

    const redirectByRole = (userDetails) => {
        if (isSuperAdmin(userDetails)) {
            navigate(getSuperAdminRedirectPath(), { replace: true });
            return;
        }
        if (localStorage.getItem(PENDING_SUBSCRIPTION_ONBOARDING_KEY) === '1') {
            navigate('/register/plans', { replace: true });
            return;
        }
        // Phones reopen on whatever page was last visited (often Planned Events),
        // so they always start on the dashboard instead of returning there.
        const isPhone = window.matchMedia('(max-width: 768px)').matches;
        const redirectTo = isPhone ? null : getRedirectPath();
        if (redirectTo) {
            navigate(redirectTo, { replace: true });
            return;
        }
        if (userDetails?.role === '1' || userDetails?.role === '2') {
            navigate('/user/dashboard', { replace: true });
        } else {
            navigate('/dashboard', { replace: true });
        }
    };

    useEffect(() => {
        // Check if user is already logged in
        const token = localStorage.getItem('token');
        const userDetails = localStorage.getItem('userDetail');
        
        if (token && token !== 'null' && token !== 'undefined' && userDetails) {
            try {
                const parsedUserDetails = JSON.parse(userDetails);
                redirectByRole(parsedUserDetails);
            } catch (error) {
                console.error('Error parsing user details:', error);
             
            }
        }
    }, [navigate]);

   const handleLogin = async () => {
    if (validateForm()) {
        setLoader(true);
        try {
            const loginData = { email, password };
            const response = await postApi('/auth/login', loginData);

            console.log('Login Response:', response);

            // Success case
            if (response?.statusCode === 200) {
                setLoader(false);

                // Save details
                localStorage.setItem("userDetail", JSON.stringify(response?.user_details));
                localStorage.setItem("token", JSON.stringify(response?.user_details?.token));
                // Every sign-in brings the Birthday Today popup back.
                clearBirthdaysTodayDismissed();
                localStorage.removeItem(PENDING_SUBSCRIPTION_ONBOARDING_KEY);
                try {
                    sessionStorage.removeItem(SELECT_FAMILY_SIDE_MODAL_DONE_SESSION_KEY);
                } catch (_) {
                    /* ignore */
                }

                // Redirect by role — super admin (role 3) skips user/admin/subscription checks
                redirectByRole(response?.user_details);
                toast.success(response?.message );
                setErrors({ email: '', password: '' });
            } 
            // Failure case (like 400)
            else {
                setLoader(false);
                toast.error(response?.message || 'Invalid credentials. Please try again.');
            }

        } catch (error) {
            setLoader(false);
            console.error('Login error:', error);

            // Handle backend HTTP errors
            if (error?.response?.status === 400) {
                toast.error(error?.response?.data?.message || 'Bad Request. Please check your details.');
            } else if (error?.response?.status === 401) {
                toast.error('Unauthorized. Invalid email or password.');
            } else {
                toast.error(error?.response?.data?.message || 'Login failed. Please try again later.');
            }
        }
    }
};


    const validateForm = () => {
        const validationErrors = {};
        if (!email) validationErrors.email = 'Email is required.';
        else if (!/\S+@\S+\.\S+/.test(email)) validationErrors.email = 'Email is invalid.';
        if (!password.trim()) validationErrors.password = 'Password is required.';
        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    return (
        <div className="login-main login-main--landing-footer">
            <div className="login-main-split login-change ">
            {/* Left Side */}
            <div className="left-div">
                <AuthBackToHome placement="bluePanel" />
                <AuthLeftBranding />
            </div>

            {/* Right Side - Form */}
            <div className="login-container login-page-panel">
                <h2>Login</h2>
                <p className="auth-page-subtitle">Please enter your credentials below that was sent to your email address. If not found in your regular email, check spam or trash to find the notification and look for famconnect26.</p>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleLogin();
                    }}
                >
                    {/* Email */}
                    <div className="form-group">
                        <label htmlFor="email" className="required-label">Email</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="form-control"
                        />
                        {errors.email && <div className="error">{errors.email}</div>}
                    </div>

                    {/* Password */}
                    <div className="form-group">
                        <label htmlFor="password" className="required-label">Password</label>
                        <div className="password-wrapper">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="form-control"
                            />
                            <span className="eye-icon" onClick={togglePasswordVisibility}>
                                <img
                                    src={
                                        showPassword
                                            ? window.getPublicUrl('/assets/images/Eye.svg')
                                            : window.getPublicUrl('/assets/images/Eye-closed.svg')
                                    }
                                    alt="Toggle Password"
                                />
                            </span>
                        </div>
                        {errors.password && <div className="error">{errors.password}</div>}
                    </div>

                    <div className="form-extra">
                        <Link to="/forgot-password" className="forgot-password">
                            Forgot Password?
                        </Link>
                    </div>

                    <Button variant="primary" type="submit" className='login-btn' disabled={loader}>
                        {loader && (
                            <div className="spinner-border" role="status">
                                <span className="sr-only"></span>
                            </div>
                        )}
                        Login
                    </Button>
                </form>
                <div className="login-signup-info">
                    <p className="login-signup-info__line">
                        <span className="signup-prompt">Family Admin?</span>{' '}
                        <Link to="/register" className="signup-link">
                            Sign Up Here to Get Started
                        </Link>
                    </p>
                    <div className="login-signup-info__group">
                        <p className="login-signup-info__line">
                            Potential users of the app, wait to get an email notification from the Admin
                        </p>
                        <p className="login-signup-info__line">
                            to obtain <strong>credentials to Login</strong> for full access.
                        </p>
                    </div>
                    <div className="login-signup-info__group">
                        <p className="login-signup-info__line">
                            Not finding the notification? Check your spam/trash folder and look for{' '}
                            <strong>FamConnect26</strong>.
                        </p>
                        <p className="login-signup-info__line">
                            Be sure to print the email out for your records.
                        </p>
                    </div>
                    <div className="login-signup-info__group">
                        <p className="login-signup-info__line">
                            If you receive the notification but your credentials are missing,
                        </p>
                        <p className="login-signup-info__line">
                            use the <strong>Forgot Password</strong> option to reset your password and log in with the new credentials.
                        </p>
                    </div>
                </div>
            </div>
            </div>

            <AuthLandingFooter />
        </div>
        
    );
}

export default Login;
